// Proportional SplitBar + authoritative numeric fields (#s05 desktop/tablet/
// phone). Save is disabled while the residual is non-zero -- compared as
// decimal STRINGS at scale 4, never floats, never integer cents (binding
// rule). Splits have no M1 write endpoint (execution plan §9.9 / WS-4 step
// 5), so Save stays disabled even at a zero residual; the reason is always
// stated, never hidden.
import { SplitBar } from '@/components/charts/SplitBar';
import { AmountText } from '@/components/AmountText';
import { decimalIsZero, decimalSubtract, decimalSum } from './decimal';
import type { LineItem } from '@/api/hooks/useTransactions';

export function SplitEditor({
  totalAmount,
  currency,
  lineItems,
}: {
  totalAmount: string;
  currency: string;
  lineItems: LineItem[];
}) {
  const allocated = decimalSum(lineItems.map((li) => li.amount));
  const residual = decimalSubtract(totalAmount, allocated);
  const hasResidual = !decimalIsZero(residual);

  return (
    <div data-testid="split-editor" className="mt-3 rounded-lg border border-border bg-card p-3.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Split · {lineItems.length} items</span>
        <span className="text-xs text-muted-foreground">Read-only</span>
      </div>

      <SplitBar
        className="mt-2.5"
        total={totalAmount}
        unallocated={residual}
        parts={lineItems.map((li) => ({
          amount: li.amount,
          label: li.category_slug ? li.category_slug.split('.').join(' › ') : 'Uncategorized',
        }))}
      />

      <div className="mt-3 flex flex-col gap-2 text-sm">
        {lineItems.map((li) => (
          <div key={li.line_item_id} className="grid grid-cols-[1fr_110px] items-center gap-2">
            <span>{li.category_slug ? li.category_slug.split('.').join(' › ') : 'Uncategorized'}</span>
            <AmountText value={li.amount} currency={currency} className="justify-self-end" />
          </div>
        ))}
      </div>

      {hasResidual && (
        <div
          data-testid="split-residual"
          className="mt-3 flex items-center justify-between border-t border-border pt-2.5 text-xs text-degraded-foreground"
        >
          <span>Unallocated · declared parts do not equal the charge</span>
          <AmountText value={residual} className="text-xs" />
        </div>
      )}

      <p role="status" className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
        Editing splits is not available yet
      </p>
    </div>
  );
}

export default SplitEditor;
